import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { Observable } from 'rxjs'

import { AttachmentsApi } from '@app/components/attachments/attachments.api'
import { PagedResponse } from '@app/components/generic/generic'
import { BaseApiV2, launchDownload } from '@app/components/generic/utilities'
import { LongRunningJobsService } from '@app/generated/api/long-running-jobs/long-running-jobs.service'
import { Attachment } from '@app/generated/api/model/attachment'
import { LongRunningJob } from '@app/generated/api/model/longRunningJob'

/**
 * list/retrieve go through the generated LongRunningJobsService (LongRunningJobService
 * injects it directly). The attachment methods below also delegate to the generated
 * service now that attachments/mixins.py's AttachmentViewSetMixin is @extend_schema
 * annotated: the generated client correctly issues blob downloads and multipart
 * uploads for this resource. This class is the pilot for that; the other 18
 * resources mixing in AttachmentViewSetMixin still use the hand-written
 * AttachmentsApi<T> mixin and can be migrated the same way later.
 */
@Injectable({ providedIn: 'root' })
export class LongRunningJobsApi extends BaseApiV2<LongRunningJob> implements AttachmentsApi<LongRunningJob, Attachment> {
  protected readonly http = inject(HttpClient)
  private readonly longRunningJobsService = inject(LongRunningJobsService)
  public readonly baseUrl = 'long-running-jobs'
  public readonly codeField = 'code'

  listAttachments (params: any): Observable<PagedResponse<Attachment>> {
    const code = params.attachmentsInstanceCode
    return this.longRunningJobsService.attachmentsList(code, params)
  }

  deleteAttachment (instanceCode: string | number, attachmentUuid: string): Observable<void> {
    return this.longRunningJobsService.attachmentsDestroy(String(instanceCode), attachmentUuid)
  }

  createAttachments (instanceCode: string | number, theFiles: File[], data: Record<string, string> = {},
    detail: Record<string, string|number|boolean|Array<string| number>> = {}): Observable<Attachment[]> {
    return this.longRunningJobsService.attachmentsCreate(String(instanceCode), {
      theFile: theFiles,
      description: data.description,
      type: data.type,
      detail: JSON.stringify(detail),
    })
  }

  getAttachmentAsBlob (instanceCode: string | number, attachmentUuid: string): Observable<Blob> {
    return this.longRunningJobsService.attachmentsDownload(String(instanceCode), attachmentUuid)
  }

  downloadAttachment (instanceCode: string | number, attachmentUuid: string): void {
    this.longRunningJobsService.attachmentsDownload(String(instanceCode), attachmentUuid, { observe: 'response' }).subscribe(
      (response) => launchDownload(response),
    )
  }
}
